import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	Bell,
	Check,
	Loader2,
	Lock,
	Mail,
	Send,
	Trash2,
	User,
} from "lucide-react";
import { useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { BrandButton } from "@/components/brand/brand-button";
import {
	BrandCard,
	BrandCardContent,
	BrandCardDescription,
	BrandCardHeader,
	BrandCardTitle,
} from "@/components/brand/brand-card";
import { BrandInput, BrandInputGroup } from "@/components/brand/brand-input";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { DashboardPageHeader } from "@/components/dashboard/DashboardPageHeader";
import { LoadingScreen } from "@/components/LoadingScreen";
import {
	ConfirmDeleteDialog,
	ConfirmDeleteDialogConfirmation,
	ConfirmDeleteDialogContent,
	ConfirmDeleteDialogFooter,
	ConfirmDeleteDialogHeader,
	ConfirmDeleteDialogTrigger,
} from "@/components/ui/confirm-delete-dialog";
import { useSession, useUpdateUser } from "@/hooks/auth-hooks";
import { authClient, signOut } from "@/lib/auth-client";
import { useWebHaptics } from "@/lib/haptics";

export const Route = createFileRoute("/dashboard/settings")({
	component: SettingsPage,
});

const pageShellClass =
	"mx-auto w-full max-w-none px-4 py-8 sm:px-6 lg:px-8 lg:py-10 xl:max-w-[1800px]";
const sectionCardClass = "bg-card shadow-none";
const sectionHeaderClass =
	"mb-5 flex flex-col gap-2 border-b border-border pb-5 sm:flex-row sm:items-start sm:justify-between";
const sectionIconClass =
	"flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground";
const sectionTitleClass = "text-base font-semibold tracking-normal";
const sectionDescriptionClass = "text-sm leading-6 text-muted-foreground";
const formClass = "grid gap-4";

const settingsSections = [
	{ id: "profile", label: "Profile", icon: User },
	{ id: "email", label: "Email address", icon: Mail },
	{ id: "notifications", label: "Browser notifications", icon: Bell },
	{ id: "password", label: "Password", icon: Lock },
	{ id: "dangerZone", label: "Danger Zone", icon: Trash2 },
] as const;

type PushPublicKeyResponse = {
	configured: boolean;
	publicKey: string | null;
};

function supportsBrowserPush() {
	return (
		typeof window !== "undefined" &&
		"Notification" in window &&
		"serviceWorker" in navigator &&
		"PushManager" in window
	);
}

function urlBase64ToUint8Array(base64String: string) {
	const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
	const base64 = `${base64String}${padding}`
		.replace(/-/g, "+")
		.replace(/_/g, "/");
	const rawData = window.atob(base64);
	return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

async function getPushWorkerRegistration() {
	return navigator.serviceWorker.register("/push-worker.js");
}

function SettingsPage() {
	const { session, user, isPending, refetch } = useSession();
	const navigate = useNavigate();
	const updateUser = useUpdateUser();
	const { trigger } = useWebHaptics();
	const settingsId = useId();
	const settingsSectionIds: Record<
		(typeof settingsSections)[number]["id"],
		string
	> = {
		profile: `${settingsId}-profile`,
		email: `${settingsId}-email`,
		notifications: `${settingsId}-notifications`,
		password: `${settingsId}-password`,
		dangerZone: `${settingsId}-danger-zone`,
	};

	// Profile state
	const [name, setName] = useState("");
	const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

	// Email state
	const [newEmail, setNewEmail] = useState("");
	const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);

	// Password state
	const [currentPassword, setCurrentPassword] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

	// Delete account state
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [deleteConfirmText, setDeleteConfirmText] = useState("");
	const [isDeleting, setIsDeleting] = useState(false);

	// Browser notification state
	const [pushSupported, setPushSupported] = useState(false);
	const [pushPermission, setPushPermission] =
		useState<NotificationPermission>("default");
	const [isPushSubscribed, setIsPushSubscribed] = useState(false);
	const [isLoadingPushSubscription, setIsLoadingPushSubscription] =
		useState(true);
	const [isUpdatingPush, setIsUpdatingPush] = useState(false);
	const [isTestingPush, setIsTestingPush] = useState(false);

	const pushConfigQuery = useQuery({
		queryKey: ["browser-push-public-key"],
		enabled: pushSupported && Boolean(session),
		queryFn: async (): Promise<PushPublicKeyResponse> => {
			const response = await fetch("/api/notifications/push/public-key", {
				headers: { Accept: "application/json" },
			});
			if (!response.ok) {
				const payload = (await response.json().catch(() => null)) as {
					error?: string;
				} | null;
				throw new Error(payload?.error || "Failed to load notification config");
			}
			return response.json();
		},
	});
	const pushConfigured = pushConfigQuery.data?.configured ?? false;
	const pushPublicKey = pushConfigQuery.data?.publicKey ?? null;
	const isLoadingPushState =
		pushSupported && (pushConfigQuery.isLoading || isLoadingPushSubscription);

	// Initialize name from user data
	useEffect(() => {
		if (user?.name) {
			setName(user.name);
		}
	}, [user?.name]);

	useEffect(() => {
		const supported = supportsBrowserPush();
		setPushSupported(supported);
		if (!supported) {
			setIsLoadingPushSubscription(false);
			return;
		}

		setPushPermission(Notification.permission);
	}, []);

	useEffect(() => {
		if (!pushSupported) return;
		if (pushConfigQuery.isLoading) return;
		if (!pushConfigQuery.data?.configured) {
			setIsLoadingPushSubscription(false);
			return;
		}

		let cancelled = false;
		setIsLoadingPushSubscription(true);
		(async () => {
			try {
				const registration = await getPushWorkerRegistration();
				const subscription = await registration.pushManager.getSubscription();
				if (!cancelled) {
					setIsPushSubscribed(Boolean(subscription));
				}
			} catch (error) {
				console.error("[Notifications] Failed to load push state", error);
			} finally {
				if (!cancelled) {
					setIsLoadingPushSubscription(false);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [
		pushSupported,
		pushConfigQuery.data?.configured,
		pushConfigQuery.isLoading,
	]);

	const handleUpdateProfile = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim()) return;

		setIsUpdatingProfile(true);
		try {
			await updateUser.mutateAsync({ name: name.trim() });
			await refetch();
			trigger("success");
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to update profile",
			);
		} finally {
			setIsUpdatingProfile(false);
		}
	};

	const handleUpdateEmail = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!newEmail.trim()) return;

		setIsUpdatingEmail(true);
		try {
			const result = await authClient.changeEmail({
				newEmail: newEmail.trim(),
			});
			if (result.error) {
				toast.error(result.error.message || "Failed to update email");
			} else {
				trigger("success");
				await refetch();
				setNewEmail("");
				toast.success(
					"Email update requested. Please check your inbox to verify.",
				);
			}
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to update email",
			);
		} finally {
			setIsUpdatingEmail(false);
		}
	};

	const handleUpdatePassword = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!currentPassword || !newPassword || !confirmPassword) return;

		if (newPassword !== confirmPassword) {
			toast.error("New passwords do not match");
			return;
		}

		if (newPassword.length < 8) {
			toast.error("Password must be at least 8 characters");
			return;
		}

		setIsUpdatingPassword(true);
		try {
			const result = await authClient.changePassword({
				currentPassword,
				newPassword,
			});
			if (result.error) {
				toast.error(result.error.message || "Failed to update password");
			} else {
				trigger("success");
				setCurrentPassword("");
				setNewPassword("");
				setConfirmPassword("");
			}
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to update password",
			);
		} finally {
			setIsUpdatingPassword(false);
		}
	};

	const handleEnableBrowserNotifications = async () => {
		if (!pushSupported || !pushConfigured || !pushPublicKey) {
			toast.error("Browser notifications are not available.");
			return;
		}

		setIsUpdatingPush(true);
		try {
			const permission = await Notification.requestPermission();
			setPushPermission(permission);
			if (permission !== "granted") {
				toast.error("Browser notification permission was not granted.");
				return;
			}

			const registration = await getPushWorkerRegistration();
			const existing = await registration.pushManager.getSubscription();
			const subscription =
				existing ??
				(await registration.pushManager.subscribe({
					applicationServerKey: urlBase64ToUint8Array(pushPublicKey),
					userVisibleOnly: true,
				}));

			const response = await fetch("/api/notifications/push/subscribe", {
				method: "POST",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					contentEncoding: "aes128gcm",
					subscription: subscription.toJSON(),
				}),
			});
			if (!response.ok) {
				const payload = (await response.json().catch(() => null)) as {
					error?: string;
				} | null;
				throw new Error(payload?.error || "Failed to enable notifications");
			}

			setIsPushSubscribed(true);
			trigger("success");
			toast.success("Browser notifications enabled");
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: "Failed to enable browser notifications",
			);
		} finally {
			setIsUpdatingPush(false);
		}
	};

	const handleDisableBrowserNotifications = async () => {
		if (!pushSupported) return;

		setIsUpdatingPush(true);
		try {
			const registration = await getPushWorkerRegistration();
			const subscription = await registration.pushManager.getSubscription();
			if (subscription) {
				await fetch("/api/notifications/push/subscribe", {
					method: "DELETE",
					headers: {
						Accept: "application/json",
						"Content-Type": "application/json",
					},
					body: JSON.stringify({ endpoint: subscription.endpoint }),
				});
				await subscription.unsubscribe();
			}

			setIsPushSubscribed(false);
			toast.success("Browser notifications disabled");
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: "Failed to disable browser notifications",
			);
		} finally {
			setIsUpdatingPush(false);
		}
	};

	const handleTestBrowserNotification = async () => {
		setIsTestingPush(true);
		try {
			const response = await fetch("/api/notifications/push/test", {
				method: "POST",
				headers: { Accept: "application/json" },
			});
			const payload = (await response.json().catch(() => null)) as {
				delivered?: number;
				subscriptionCount?: number;
				error?: string;
			} | null;
			if (!response.ok) {
				throw new Error(payload?.error || "Failed to send test notification");
			}
			if (!payload?.subscriptionCount) {
				toast.error("No active browser notification subscription found.");
			} else if ((payload.delivered ?? 0) > 0) {
				toast.success("Test notification sent");
			} else {
				toast.error("The test notification could not be delivered.");
			}
		} catch (error) {
			toast.error(
				error instanceof Error
					? error.message
					: "Failed to send test notification",
			);
		} finally {
			setIsTestingPush(false);
		}
	};

	const handleDeleteAccount = async () => {
		if (deleteConfirmText !== "DELETE") return;

		setIsDeleting(true);
		try {
			const result = await authClient.deleteUser();
			if (result.error) {
				toast.error(result.error.message || "Failed to delete account");
			} else {
				setDeleteDialogOpen(false);
				await signOut();
				navigate({ to: "/" });
			}
		} catch (err) {
			toast.error(
				err instanceof Error ? err.message : "Failed to delete account",
			);
		} finally {
			setIsDeleting(false);
		}
	};

	if (isPending) {
		return <LoadingScreen />;
	}

	if (!session || !user) {
		return (
			<div className="min-h-svh flex items-center justify-center bg-background">
				<BrandCard
					variant="elevated"
					padding="lg"
					className="max-w-md text-center"
				>
					<BrandCardHeader>
						<BrandCardTitle>Sign in required</BrandCardTitle>
						<BrandCardDescription>
							Please sign in to access your settings
						</BrandCardDescription>
					</BrandCardHeader>
					<BrandCardContent className="pt-6">
						<BrandButton onClick={() => navigate({ to: "/signup" })}>
							Sign In
						</BrandButton>
					</BrandCardContent>
				</BrandCard>
			</div>
		);
	}

	return (
		<DashboardLayout className="flex flex-col pb-0 lg:flex-row lg:overflow-hidden">
			<aside className="hidden w-[17rem] shrink-0 flex-col border-r border-border bg-muted/35 lg:flex">
				<div className="border-b border-border px-4 py-5">
					<DashboardPageHeader
						className="[&_h1]:text-2xl [&_h1]:sm:text-2xl"
						title="Settings"
						description="Manage your identity, sign-in details, and account access."
					/>
				</div>
				<nav
					aria-label="Settings sections"
					className="flex-1 overflow-y-auto px-2 py-2"
				>
					<div className="space-y-1">
						{settingsSections.map((section) => {
							const Icon = section.icon;
							return (
								<a
									key={section.id}
									href={`#${settingsSectionIds[section.id]}`}
									className="group flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-muted-foreground opacity-70 transition-all duration-200 hover:bg-background/70 hover:text-foreground hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
								>
									<span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground transition-colors group-hover:text-foreground">
										<Icon className="size-4" />
									</span>
									<span className="min-w-0 flex-1 truncate text-sm font-medium leading-5">
										{section.label}
									</span>
								</a>
							);
						})}
					</div>
				</nav>
			</aside>

			<div className="min-w-0 flex-1 overflow-y-auto">
				<div className={pageShellClass}>
					<DashboardPageHeader
						className="mb-8 lg:hidden"
						title="Settings"
						description="Manage your identity, sign-in details, and account access."
					/>
					<div className="space-y-4">
						{/* Profile Settings */}
						<BrandCard
							id={settingsSectionIds.profile}
							variant="default"
							padding="lg"
							className={sectionCardClass}
						>
							<BrandCardHeader className={sectionHeaderClass}>
								<div className="flex items-start gap-3">
									<div className={sectionIconClass}>
										<User className="size-4" />
									</div>
									<div>
										<BrandCardTitle className={sectionTitleClass}>
											Profile
										</BrandCardTitle>
										<BrandCardDescription className={sectionDescriptionClass}>
											Update your display name
										</BrandCardDescription>
									</div>
								</div>
							</BrandCardHeader>
							<BrandCardContent>
								<form onSubmit={handleUpdateProfile} className={formClass}>
									<BrandInputGroup
										label="Display name"
										className="[&_label]:text-sm [&_p]:text-sm"
									>
										<BrandInput
											className="h-9 text-sm file:text-sm"
											icon={<User className="w-4 h-4" />}
											value={name}
											onChange={(e) => setName(e.target.value)}
											placeholder={user.name || "Enter your name"}
										/>
									</BrandInputGroup>
									<BrandButton
										type="submit"
										disabled={isUpdatingProfile || !name.trim()}
										size="sm"
									>
										{isUpdatingProfile ? (
											<>
												<div className="animate-spin h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
												Updating...
											</>
										) : (
											<>
												<Check className="w-4 h-4" />
												Save Changes
											</>
										)}
									</BrandButton>
								</form>
							</BrandCardContent>
						</BrandCard>

						{/* Email Settings */}
						<BrandCard
							id={settingsSectionIds.email}
							variant="default"
							padding="lg"
							className={sectionCardClass}
						>
							<BrandCardHeader className={sectionHeaderClass}>
								<div className="flex items-start gap-3">
									<div className={sectionIconClass}>
										<Mail className="size-4" />
									</div>
									<div>
										<BrandCardTitle className={sectionTitleClass}>
											Email address
										</BrandCardTitle>
										<BrandCardDescription className={sectionDescriptionClass}>
											Change the email used for sign-in and notifications.
										</BrandCardDescription>
									</div>
								</div>
							</BrandCardHeader>
							<BrandCardContent>
								<form onSubmit={handleUpdateEmail} className={formClass}>
									<BrandInputGroup
										label="New email address"
										className="[&_label]:text-sm [&_p]:text-sm"
									>
										<BrandInput
											type="email"
											className="h-9 text-sm file:text-sm"
											icon={<Mail className="w-4 h-4" />}
											value={newEmail}
											onChange={(e) => setNewEmail(e.target.value)}
											placeholder={user.email}
										/>
									</BrandInputGroup>
									<p className="text-xs leading-5 text-muted-foreground">
										A verification email will be sent to your new address.
									</p>
									<BrandButton
										type="submit"
										disabled={
											isUpdatingEmail ||
											!newEmail.trim() ||
											newEmail.trim() === user.email
										}
										size="sm"
									>
										{isUpdatingEmail ? (
											<>
												<div className="animate-spin h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
												Updating...
											</>
										) : (
											<>
												<Check className="w-4 h-4" />
												Update Email
											</>
										)}
									</BrandButton>
								</form>
							</BrandCardContent>
						</BrandCard>

						{/* Browser Notifications */}
						<BrandCard
							id={settingsSectionIds.notifications}
							variant="default"
							padding="lg"
							className={sectionCardClass}
						>
							<BrandCardHeader className={sectionHeaderClass}>
								<div className="flex items-start gap-3">
									<div className={sectionIconClass}>
										<Bell className="size-4" />
									</div>
									<div>
										<BrandCardTitle className={sectionTitleClass}>
											Browser notifications
										</BrandCardTitle>
										<BrandCardDescription className={sectionDescriptionClass}>
											Receive failed-post alerts on this device.
										</BrandCardDescription>
									</div>
								</div>
							</BrandCardHeader>
							<BrandCardContent>
								<div className="space-y-4">
									<div className="flex flex-col gap-3 rounded-md border border-border bg-muted/20 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
										<div>
											<p className="text-sm font-medium text-foreground">
												{isLoadingPushState
													? "Checking notification status"
													: isPushSubscribed
														? "Enabled"
														: "Disabled"}
											</p>
											<p className="mt-1 text-xs leading-5 text-muted-foreground">
												{!pushSupported
													? "This browser does not support push notifications."
													: !pushConfigured
														? "Push notifications are not configured for this environment."
														: pushPermission === "denied"
															? "Notification permission is blocked in this browser."
															: isPushSubscribed
																? "Failed publish alerts can be delivered here."
																: "Failed publish alerts are not active on this device."}
											</p>
										</div>
										<div className="flex shrink-0 flex-wrap gap-2">
											{isPushSubscribed ? (
												<BrandButton
													type="button"
													size="sm"
													onClick={handleDisableBrowserNotifications}
													disabled={isUpdatingPush || isLoadingPushState}
												>
													{isUpdatingPush ? (
														<Loader2 className="size-4 animate-spin" />
													) : (
														<Bell className="size-4" />
													)}
													Disable
												</BrandButton>
											) : (
												<BrandButton
													type="button"
													size="sm"
													onClick={handleEnableBrowserNotifications}
													disabled={
														isUpdatingPush ||
														isLoadingPushState ||
														!pushSupported ||
														!pushConfigured ||
														pushPermission === "denied"
													}
												>
													{isUpdatingPush ? (
														<Loader2 className="size-4 animate-spin" />
													) : (
														<Bell className="size-4" />
													)}
													Enable
												</BrandButton>
											)}
											<BrandButton
												type="button"
												size="sm"
												onClick={handleTestBrowserNotification}
												disabled={
													isTestingPush ||
													isLoadingPushState ||
													!isPushSubscribed
												}
											>
												{isTestingPush ? (
													<Loader2 className="size-4 animate-spin" />
												) : (
													<Send className="size-4" />
												)}
												Test
											</BrandButton>
										</div>
									</div>
								</div>
							</BrandCardContent>
						</BrandCard>

						{/* Password Settings */}
						<BrandCard
							id={settingsSectionIds.password}
							variant="default"
							padding="lg"
							className={sectionCardClass}
						>
							<BrandCardHeader className={sectionHeaderClass}>
								<div className="flex items-start gap-3">
									<div className={sectionIconClass}>
										<Lock className="size-4" />
									</div>
									<div>
										<BrandCardTitle className={sectionTitleClass}>
											Password
										</BrandCardTitle>
										<BrandCardDescription className={sectionDescriptionClass}>
											Change your password to keep your account secure.
										</BrandCardDescription>
									</div>
								</div>
							</BrandCardHeader>
							<BrandCardContent>
								<form onSubmit={handleUpdatePassword} className={formClass}>
									<BrandInputGroup
										label="Current Password"
										className="[&_label]:text-sm [&_p]:text-sm"
									>
										<BrandInput
											type="password"
											className="h-9 text-sm file:text-sm"
											autoComplete="current-password"
											icon={<Lock className="w-4 h-4" />}
											value={currentPassword}
											onChange={(e) => setCurrentPassword(e.target.value)}
											placeholder="Enter your current password"
										/>
									</BrandInputGroup>
									<BrandInputGroup
										label="New Password"
										className="[&_label]:text-sm [&_p]:text-sm"
									>
										<BrandInput
											type="password"
											className="h-9 text-sm file:text-sm"
											autoComplete="new-password"
											icon={<Lock className="w-4 h-4" />}
											value={newPassword}
											onChange={(e) => setNewPassword(e.target.value)}
											placeholder="Enter your new password"
										/>
									</BrandInputGroup>
									<BrandInputGroup
										label="Confirm New Password"
										className="[&_label]:text-sm [&_p]:text-sm"
									>
										<BrandInput
											type="password"
											className="h-9 text-sm file:text-sm"
											autoComplete="new-password"
											icon={<Lock className="w-4 h-4" />}
											value={confirmPassword}
											onChange={(e) => setConfirmPassword(e.target.value)}
											placeholder="Confirm your new password"
										/>
									</BrandInputGroup>
									<BrandButton
										type="submit"
										disabled={
											isUpdatingPassword ||
											!currentPassword ||
											!newPassword ||
											!confirmPassword
										}
										size="sm"
									>
										{isUpdatingPassword ? (
											<>
												<div className="animate-spin h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
												Updating...
											</>
										) : (
											<>
												<Check className="w-4 h-4" />
												Update Password
											</>
										)}
									</BrandButton>
								</form>
							</BrandCardContent>
						</BrandCard>

						{/* Danger Zone */}
						<BrandCard
							id={settingsSectionIds.dangerZone}
							variant="default"
							padding="lg"
							className="border-destructive/25 bg-card shadow-none"
						>
							<BrandCardHeader className={sectionHeaderClass}>
								<div className="flex items-start gap-3">
									<div className="flex size-9 shrink-0 items-center justify-center rounded-md border border-destructive/25 bg-background text-destructive">
										<Trash2 className="size-4" />
									</div>
									<div>
										<BrandCardTitle className="text-base font-semibold tracking-normal text-destructive">
											Danger Zone
										</BrandCardTitle>
										<BrandCardDescription className={sectionDescriptionClass}>
											Permanently delete your account
										</BrandCardDescription>
									</div>
								</div>
							</BrandCardHeader>
							<BrandCardContent>
								<div className="space-y-4">
									<p className="text-sm text-muted-foreground">
										Once you delete your account, all your data will be
										permanently removed. This action cannot be undone.
									</p>
									<ConfirmDeleteDialog
										open={deleteDialogOpen}
										onOpenChange={(open) => {
											setDeleteDialogOpen(open);
											if (!open) setDeleteConfirmText("");
										}}
									>
										<ConfirmDeleteDialogTrigger asChild>
											<BrandButton type="button" size="sm">
												<Trash2 className="w-4 h-4" />
												Delete Account
											</BrandButton>
										</ConfirmDeleteDialogTrigger>
										<ConfirmDeleteDialogContent>
											<ConfirmDeleteDialogHeader
												title="Delete Account"
												description="This will permanently delete your account, all your businesses, reviews, and videos. This action cannot be undone."
											/>
											<ConfirmDeleteDialogConfirmation
												value={deleteConfirmText}
												onChange={setDeleteConfirmText}
												confirmText="DELETE"
												placeholder="Type DELETE"
											/>
											<ConfirmDeleteDialogFooter
												onCancel={() => {
													setDeleteDialogOpen(false);
													setDeleteConfirmText("");
												}}
												onConfirm={handleDeleteAccount}
												confirmDisabled={deleteConfirmText !== "DELETE"}
												isLoading={isDeleting}
												confirmLabel="Delete My Account"
											/>
										</ConfirmDeleteDialogContent>
									</ConfirmDeleteDialog>
								</div>
							</BrandCardContent>
						</BrandCard>
					</div>
				</div>
			</div>
		</DashboardLayout>
	);
}
